import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  IconButton,
  Input,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as Yup from 'yup';

import useGetVehicleTypes from '../../apiHooks/useGetVehicleTypesApi';
import { showError, showToast } from '../../constants/toasts';
import { Loader } from '../../helpers';
import MainContainer from '../../layout/MainContainer';
import {
  useAddBodyTypeMutation,
  useLazyGetBodyTypeByIdQuery,
  useUpdateBodyTypeMutation,
} from '../../service/bodyType';
import { CommonBody } from '../../types/General';
import { generateEncryptedKeyBody } from '../../utils/crypto';
import { UploadMedia } from '../../utils/mediaUpload';

const AddBodyType = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [image, setImage] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vehicleType, setVehicleType] = useState<any>('default');

  const { data, load } = useGetVehicleTypes();

  const [addBodyType] = useAddBodyTypeMutation();
  const [updateBodyType] = useUpdateBodyTypeMutation();
  const [getDetail] = useLazyGetBodyTypeByIdQuery();

  const handleVehicleType = (event: SelectChangeEvent) => {
    setVehicleType(event.target.value as any);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setImage('');
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data?.image) {
      setImage(imageUrl?.data?.image || '');
    }
  };

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: '',
    },

    validationSchema: Yup.object({
      name: Yup.string()
        .required('This field is required')
        .min(2, 'Minimum 2 characters are required'),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);
      if (!image || vehicleType === 'default') {
        return;
      }
      const body = {
        image: image,
        name: values?.name || '',
        ...(id ? {} : { vehicleId: vehicleType }),
      };

      if (id) {
        try {
          setLoading(true);
          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await updateBodyType({
            body: encryptedBody,
            id,
          }).unwrap();
          setLoading(false);

          if (response?.statusCode === 200) {
            showToast('Body type updated successfully');
            navigate('/vehicle-specifications/body-type');
          }
        } catch (error: any) {
          setLoading(false);

          showError(error?.data?.message || '');
          console.error(error);
        }
      } else {
        try {
          setLoading(true);

          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await addBodyType({
            body: encryptedBody,
          }).unwrap();
          setLoading(false);

          if (response?.statusCode === 200) {
            showToast('Body type added successfully');
            navigate('/vehicle-specifications/body-type');
          }
        } catch (error: any) {
          setLoading(false);
          showError(error?.data?.message || '');
          console.error(error);
        }
      }
    },
  });

  const getData = async (id: string) => {
    setLoading(true);
    try {
      const response = await getDetail({
        id,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        formik.setFieldValue('name', response?.data?.name);
        setImage(response?.data?.image);
        setVehicleType(response?.data?.vehicle?._id);
      }
    } catch (error: any) {
      showError(error?.data?.message || '');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getData(id);
    }
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={load || loading} />
      <div className="main_title fdx">
        <div>
          <h1>{id ? 'Edit' : 'Add'} Body Type</h1>
          <p>
            <span onClick={() => navigate('/dashboard')}>Dashboard</span> -{' '}
            <span onClick={() => navigate('/vehicle-specifications/body-type')}>
              Vehicle Specifications
            </span>{' '}
            - <span onClick={() => navigate('/vehicle-specifications/body-type')}>Body Types</span>{' '}
            - {id ? 'Edit' : 'Add'}
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_orange" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Image</label>
              <div className="upload_box">
                <label>
                  {image && (
                    <IconButton className="crossIcon" onClick={() => setImage('')}>
                      <CloseIcon />
                    </IconButton>
                  )}
                  <figure>
                    <img src={image ? image : '/static/images/picture.png'} alt="Upload" />
                  </figure>
                  <IconButton className="uploadIcon">
                    <label htmlFor="icon-button-file-profile">
                      <Button component="span" className="upload_image_btn">
                        <img src="/static/images/edit2_icon.svg" alt="Upload" />
                        <Input
                          sx={{ display: 'none' }}
                          id="icon-button-file-profile"
                          type="file"
                          inputProps={{
                            accept: 'image/png,image/jpeg',
                          }}
                          onChange={(e: any) => {
                            handleImageUpload(e);
                            e.target.value = null;
                          }}
                        />
                      </Button>
                    </label>
                  </IconButton>
                </label>
                {error && !image ? <h6 className="err_msg">Image is required</h6> : null}
              </div>
            </div>
            <div className="control_group w_50" />
            <div className="control_group w_50">
              <label>Select Vehicle Type</label>
              <Select
                fullWidth
                labelId="vehicleType"
                id="vehicleType"
                value={vehicleType}
                disabled={id ? true : false}
                onChange={handleVehicleType}
              >
                <MenuItem disabled value="default">
                  Select Vehicle Type
                </MenuItem>
                {data?.length
                  ? data?.map((item, i: number) => {
                      return (
                        <MenuItem value={item?._id} key={i}>
                          {item?.name || '-'}
                        </MenuItem>
                      );
                    })
                  : null}
              </Select>
              {error && vehicleType === 'default' ? (
                <h6 className="err_msg">Vehicle type is required</h6>
              ) : null}
            </div>
            <div className="control_group w_50">
              <label>Title</label>
              <TextField
                variant="outlined"
                placeholder="Title"
                fullWidth
                hiddenLabel
                name="name"
                id="name"
                className="text_field"
                inputProps={{ maxLength: 30 }}
                value={formik.values.name}
                onChange={(val) => {
                  if (val.target.value === ' ' || val.target.value === '.') {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.name && formik.errors.name}
              />
            </div>
          </div>

          <div className="form_end_btn">
            <Button className="btn btn_secondary" type="submit" onClick={() => setError(true)}>
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddBodyType;
